import { Editor } from '@tiptap/core';

type EditorWithInternals = Editor & {
  commandManager?: unknown | null;
};

const PATCH_FLAG = '__aiDovutSafeEditorPatch';

const isEditorCommandManagerReady = (editor: Editor) => {
  const editorWithInternals = editor as EditorWithInternals;

  return !editor.isDestroyed && Boolean(editorWithInternals.commandManager);
};

const disabledChain = new Proxy(() => disabledChain, {
  apply: () => disabledChain,
  get: (_target, property) => {
    if (property === 'run') {
      return () => false;
    }

    if (property === 'then') {
      return undefined;
    }

    return () => disabledChain;
  },
}) as unknown as ReturnType<Editor['chain']>;

const disabledCan = new Proxy(() => false, {
  apply: () => false,
  get: (_target, property) => {
    if (property === 'chain') {
      return () => disabledChain;
    }

    if (property === 'run') {
      return () => false;
    }

    if (property === 'then') {
      return undefined;
    }

    return () => false;
  },
}) as unknown as ReturnType<Editor['can']>;

const disabledCommands = new Proxy(
  {},
  {
    get: (_target, property) => {
      if (property === 'then') {
        return undefined;
      }

      return () => false;
    },
  },
) as Editor['commands'];

const editorPrototype = Editor.prototype as Editor['prototype'] & {
  [PATCH_FLAG]?: true;
};

if (!editorPrototype[PATCH_FLAG]) {
  const originalCan = Editor.prototype.can;
  const originalChain = Editor.prototype.chain;
  const originalCommands = Object.getOwnPropertyDescriptor(Editor.prototype, 'commands');

  Object.defineProperty(editorPrototype, PATCH_FLAG, {
    value: true,
  });

  Editor.prototype.can = function safeCan(this: Editor) {
    if (!isEditorCommandManagerReady(this)) {
      return disabledCan;
    }

    return originalCan.call(this);
  };

  Editor.prototype.chain = function safeChain(this: Editor) {
    if (!isEditorCommandManagerReady(this)) {
      return disabledChain;
    }

    return originalChain.call(this);
  };

  if (originalCommands?.get) {
    Object.defineProperty(Editor.prototype, 'commands', {
      configurable: originalCommands.configurable,
      enumerable: originalCommands.enumerable,
      get() {
        if (!isEditorCommandManagerReady(this)) {
          return disabledCommands;
        }

        return originalCommands.get?.call(this);
      },
    });
  }
}
